import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import {
  BaseAdminScreen,
  AdminEmptyScreenLayout,
} from '@/components';
import { ResourcesSkeleton } from '@/components/admin/resources/ResourcesSkeleton';
import { ResourceGrid } from '@/components/admin/resources/ResourceGrid';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useResources } from '@/hooks/admin/useResources';
import { ICommunityResource } from '@/types/admin/resources';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { EMPTY_MESSAGES } from '@/constants/admin';
import { InlineEmptyMessage } from '@/components';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { DesktopResourcesView } from './desktop/DesktopResourcesView';

type ResourcesScreenProps = StackScreenProps<AdminStackParamList, 'Resources'>;

export const ResourcesScreen: React.FC<ResourcesScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const { redesign, semantic, admin } = useAdminColors();
  const [selectedResource, setSelectedResource] = useState<ICommunityResource | null>(null);
  const { showApiError } = useErrorToast();
  const { showSuccess } = useToast();

  const {
    data,
    resources,
    generateResourceTiles,
    isLoading,
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    refresh,
    highestPosition,
    handleAssignEmptySlot,
    handleUpdateResourcePositions,
  } = useResources();



  // Show toast for API errors
  React.useEffect(() => {
    if (enhancedError) {
      showApiError(enhancedError.originalError, enhancedError.userFriendlyMessage);
    }
  }, [enhancedError, showApiError]);

  // Handle edit resource
  const handleEditResource = useCallback((resource: ICommunityResource) => {
    navigation.navigate('EditResource', { resource });
  }, [navigation]);

  // Handle create resource
  const handleCreateResource = useCallback(() => {
    navigation.navigate('CreateResource', { highestPosition });
  }, [navigation, highestPosition]);

  // Determine if we should show data or empty state
  const hasData = resources.length > 0;

  // Show error state if we have a critical error and no data
  const showErrorState = enhancedError && !hasData && !isLoading;

  // Show loading state if we're loading and have no data yet
  const showLoadingState = isLoading && !hasData && !showErrorState;

  // Render error state component
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={semantic.status.error} />
      <Text style={[styles.errorTitle, { color: redesign.text.primary }]}>Unable to Load Resources</Text>
      <Text style={[styles.errorMessage, { color: redesign.text.muted }]}>
        {enhancedError?.userFriendlyMessage || 'Something went wrong while loading the resources.'}
      </Text>

      {retryCount > 0 && (
        <Text style={[styles.retryInfo, { color: redesign.text.subtle }]}>
          Retry attempts: {retryCount}
        </Text>
      )}

      {canRetry && (
        <TouchableOpacity
          style={[styles.retryButton, { backgroundColor: admin.formInput.borderFocused }]}
          onPress={manualRetry}
        >
          <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
          <Text style={styles.retryButtonText}>Try Again</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  // Render empty state message
  const renderEmptyMessage = () => (
    <InlineEmptyMessage
      title={EMPTY_MESSAGES.resources.default.title}
      message={EMPTY_MESSAGES.resources.default.message}
      icon={EMPTY_MESSAGES.resources.default.icon}
      containerStyle={styles.emptyMessageContainer}
      titleStyle={{ ...styles.emptyMessageText, color: redesign.text.primary }}
      messageStyle={{ ...styles.emptyMessageSubtext, color: redesign.text.muted }}
    />
  );

  // Render content based on state (mobile)
  const renderContent = () => {
    if (showLoadingState) {
      return <ResourcesSkeleton />;
    }

    if (showErrorState) {
      return renderErrorState();
    }

    if (hasData) {
      return (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <ResourceGrid
            tiles={generateResourceTiles}
            // onResourcePress={handleResourcePress}
            onEditResource={handleEditResource}
            onUpdateResourcePositions={handleUpdateResourcePositions}
            onEmptySlotPress={async (position) => {
              // If a resource is selected, move it here; otherwise open create
              const selectedId = selectedResource?.id ?? null;
              if (selectedId != null) {
                // Move using hook logic
                try {
                  await handleAssignEmptySlot(selectedId, position);
                  showSuccess('Resource moved');
                  setSelectedResource(null);
                } catch (e) {
                  // Errors already handled in hook
                }
              } else {
                handleCreateResource();
              }
            }}
            onCreateResource={handleCreateResource}
            isEditable={true}
            loading={isLoading}
            selectedResourceId={selectedResource?.id ?? null}
            onSelectResource={(resourceId) => {
              if (resourceId == null) {
                setSelectedResource(null);
                return;
              }
              const res = resources.find(r => r.id === resourceId) || null;
              setSelectedResource(res);
            }}
          />
        </ScrollView>
      );
    }

    return renderEmptyMessage();
  };

  // Desktop web rendering
  if (layoutMode === 'desktop') {
    const tiles = useMemo(() => generateResourceTiles, [generateResourceTiles]);
    return (
      <AdminErrorBoundary context="ResourcesScreenDesktop">
        <DesktopResourcesView
          tiles={tiles as any}
          isLoading={isLoading}
          highestPosition={highestPosition}
          onCreateResource={handleCreateResource}
          onEditResource={handleEditResource}
          onAssignEmptySlot={handleAssignEmptySlot}
          onUpdateResourcePositions={handleUpdateResourcePositions}
          selectedResourceId={selectedResource?.id ?? null}
          onSelectResource={(resourceId) => {
            if (resourceId == null) {
              setSelectedResource(null);
              return;
            }
            const res = resources.find(r => r.id === resourceId) || null;
            setSelectedResource(res);
          }}
          onRefresh={refresh}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="ResourcesScreen">
      <BaseAdminScreen
        screenName="Resources"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Resources"
          emptyStateType="resources"
          onAddPress={handleCreateResource}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
          disableScrollView={true} // Keep wrapper stable to avoid TextInput blur/remount issues
        >
          {renderContent()}
        </AdminEmptyScreenLayout>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },

  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
    maxWidth: 300,
  },
  retryInfo: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // Empty message styles
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyMessageIcon: {
    marginBottom: 16,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
});
